import React, { memo, ReactElement, useCallback, Fragment, useMemo } from 'react';
import { match, P } from 'ts-pattern';
import {
  ContactInteractionFacebookMessenger,
  ContactInteractionInstagram,
  ContactInteractionSms,
  ContactInteractionWhatsapp,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { contactInteractionPatterns } from '@lgg/isomorphic/utils/match-contact-interaction';
import { TextFormatter } from 'src/components/general/text-formatter';
import { AttachmentItem } from 'src/components/pages/conversations/components/contact-interactions/items/attachment-items/attachment-item';
import {
  BubbleDivider,
  BubbleMessageHeader,
  MessageBubbleWithDirection,
  FailedMessageIndicator,
  RepliedMessageRenderer,
  ReferralItemRenderer,
  ItemMessage,
  InteractionTextContent,
  AutomatedMessageBubbleHeader,
  Content,
  FlowResponse,
  RepliedStoryRenderer,
  MessageBubbleWithDirectionContainerVariant,
} from 'src/components/pages/conversations/components/contact-interactions/items/shared';
import {
  getFirstReactionFromContactInteractionComponent,
  getMessageComponentReaction,
  ReactionsWrapper,
} from 'src/components/pages/conversations/components/contact-interactions/items/whatsapp/reactions/reactions-wrapper';
import { ContactInteractionDetailsCaretVariant } from './contact-interaction-details';

export type ContactInteractionsWithAttachmentUnion =
  | ContactInteractionFacebookMessenger
  | ContactInteractionSms
  | ContactInteractionWhatsapp
  | ContactInteractionInstagram;

type TextItemProps = {
  contactInteraction: ContactInteractionsWithAttachmentUnion;
  titleBuilder: () => ReactElement;
  testId: string;
  conversationId: string;
  messageSanitizer?: (message: string) => string;
};

export const TextItem = memo<TextItemProps>(
  ({ contactInteraction, conversationId, titleBuilder, testId, messageSanitizer }) => {
    const {
      direction,
      message,
      occurredAt,
      registrationType,
      description,
      attachments,
      id,
      campaign,
      source,
      isMasked,
      department,
      participatingEntity,
      createdByUser,
    } = contactInteraction;

    const fromResource = match(contactInteraction)
      .with(
        P.union(contactInteractionPatterns.sms, contactInteractionPatterns.whatsapp),
        (interaction) => interaction.resourceAddress,
      )
      .otherwise(() => null);

    const contactAddress = match(contactInteraction)
      .with(
        P.union(contactInteractionPatterns.sms, contactInteractionPatterns.whatsapp),
        (interaction) => interaction.contactAddress,
      )
      .otherwise(() => null);

    const showDeliveryStatus = contactInteraction.direction === 'OUTBOUND';
    const showFailedMessageError = useCallback(
      (entity) => showDeliveryStatus && entity?.status === 'FAILED',
      [showDeliveryStatus],
    );

    const messageReaction = getMessageComponentReaction(contactInteraction);
    const baseDetails = useMemo(() => {
      return {
        interactionId: id,
        campaign,
        source,
        conversationId,
        department,
        contactAddress,
        resourceAddress: fromResource,
        createdBy: createdByUser?.id,
      };
    }, [
      campaign,
      contactAddress,
      conversationId,
      department,
      fromResource,
      id,
      source,
      createdByUser?.id,
    ]);

    const renderDescription = (
      params: Pick<
        ContactInteractionsWithAttachmentUnion,
        'direction' | 'description' | 'registrationType'
      >,
    ) => {
      const { direction, description = '', registrationType } = params;

      return (
        <InteractionTextContent direction={direction}>
          {registrationType === 'MANUAL' ? (
            <TextFormatter>{description}</TextFormatter>
          ) : (
            description
          )}
        </InteractionTextContent>
      );
    };

    const isAutomatedMessage = useMemo(() => {
      return match(participatingEntity)
        .with({ type: P.union('BROADCAST', 'CALLFLOW') }, () => true)
        .otherwise(() => false);
    }, [participatingEntity]);

    const isAiAgentMessage = useMemo(() => {
      return match(participatingEntity)
        .with({ type: 'AI_AGENT' }, () => true)
        .otherwise(() => false);
    }, [participatingEntity]);

    const getAutomatedMessageBubbleTitle = useCallback(() => {
      return match(participatingEntity)
        .with({ type: 'CALLFLOW' }, ({ label }) => (
          <AutomatedMessageBubbleHeader icon="callflow" title={label} />
        ))
        .with({ type: 'BROADCAST' }, ({ label }) => (
          <AutomatedMessageBubbleHeader icon="broadcast" title={label} />
        ))
        .otherwise(() => null);
    }, [participatingEntity]);

    const renderTextMessageContent = (
      params: Pick<ContactInteractionsWithAttachmentUnion, 'direction' | 'message'>,
    ) => {
      const { direction, message } = params;

      return (
        <>
          {isAutomatedMessage ? (
            <>
              {getAutomatedMessageBubbleTitle()}
              <BubbleDivider direction={direction} />
            </>
          ) : null}
          <Content isBlurred={isMasked}>
            <ItemMessage data-lgg-id="text-item-message" direction={direction}>
              {message?.content ? (
                <TextFormatter>
                  {messageSanitizer ? messageSanitizer(message.content) : message.content}
                </TextFormatter>
              ) : null}
            </ItemMessage>
          </Content>
        </>
      );
    };

    const messageContent = match(contactInteraction.message)
      .with(
        {
          type: 'JSON',
        },
        ({ content }) => <FlowResponse responses={content} />,
      )
      .otherwise((message) => renderTextMessageContent({ direction, message }));

    const messageBubbleVariant = useMemo(
      () =>
        match(message)
          .with(
            { referral: P.nonNullable },
            () => MessageBubbleWithDirectionContainerVariant.REFERRAL,
          )
          .with(
            { repliedStory: P.nonNullable },
            () => MessageBubbleWithDirectionContainerVariant.STORY_REPLY,
          )
          .otherwise(() => MessageBubbleWithDirectionContainerVariant.REGULAR),
      [message],
    );

    return (
      <Fragment key={contactInteraction.id}>
        {message?.content || registrationType === 'MANUAL' ? (
          <Fragment key={contactInteraction.id}>
            <ReactionsWrapper
              reactions={messageReaction ? [messageReaction] : messageReaction}
              direction={contactInteraction.direction}
            >
              <MessageBubbleWithDirection
                testId={testId}
                direction={direction}
                registrationType={registrationType}
                createdAt={occurredAt}
                details={{ ...baseDetails, message: message?.content ?? description }}
                deliveryStatus={showDeliveryStatus ? message?.status : undefined}
                $variant={messageBubbleVariant}
                backgroundColor={
                  isAutomatedMessage || isAiAgentMessage ? 'secondaryMint20' : undefined
                }
                caretVariantOverride={
                  isAutomatedMessage || isAiAgentMessage
                    ? ContactInteractionDetailsCaretVariant.CLEAR
                    : undefined
                }
              >
                {registrationType === 'MANUAL' ? (
                  <>
                    <BubbleMessageHeader
                      contactInteraction={contactInteraction}
                      title={titleBuilder()}
                    />
                    <BubbleDivider direction={direction} />
                    {renderDescription({ direction, description, registrationType })}
                  </>
                ) : (
                  <>
                    <RepliedMessageRenderer contactInteraction={contactInteraction} />
                    <ReferralItemRenderer contactInteraction={contactInteraction} />
                    <RepliedStoryRenderer contactInteraction={contactInteraction} />
                    {messageContent}
                  </>
                )}
              </MessageBubbleWithDirection>
            </ReactionsWrapper>
            {showFailedMessageError(message) && <FailedMessageIndicator {...message} />}
          </Fragment>
        ) : null}
        {[...attachments].reverse().map((attachment) => {
          const reaction = getFirstReactionFromContactInteractionComponent(attachment);

          return (
            <Fragment key={attachment.id}>
              <ReactionsWrapper
                reactions={reaction ? [reaction] : reaction}
                direction={contactInteraction.direction}
              >
                <AttachmentItem
                  contactInteraction={contactInteraction}
                  attachment={attachment}
                  detailsPopoverProps={baseDetails}
                  showDeliveryStatus={showDeliveryStatus}
                />
              </ReactionsWrapper>
              {showFailedMessageError(attachment) && (
                <FailedMessageIndicator {...attachment} />
              )}
            </Fragment>
          );
        })}
      </Fragment>
    );
  },
);
